<template>
  <div class="profile-content">
    <div class="main-content">
      <div class="page-header">
        <h1 class="page-title">User Profile</h1>
      </div>

      <!-- User Information Card -->
      <v-card class="user-info-card mb-6" elevation="3">
        <v-card-title class="card-header">
          <v-icon left color="white">mdi-account-circle</v-icon>
          <span>Account Information</span>
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <div class="info-item">
                <span class="info-label">Full Name:</span>
                <span class="info-value">{{ userData.fullName || 'N/A' }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <span class="info-label">Agent ID:</span>
                <span class="info-value">{{ getAgentId() }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <span class="info-label">Email:</span>
                <span class="info-value">{{ userData.email || 'N/A' }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <span class="info-label">Role:</span>
                <span class="info-value">{{ userData.role || 'User' }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <span class="info-label">Department:</span>
                <span class="info-value">{{ userData.department || 'N/A' }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <span class="info-label">Phone:</span>
                <span class="info-value">{{ userData.phoneNumber || 'N/A' }}</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Recent Login History -->
      <v-card class="history-card" elevation="3">
        <v-card-title class="card-header">
          <v-icon left color="white">mdi-history</v-icon>
          <span>Recent Login History</span>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="historyHeaders"
            :items="recentLogins"
            :items-per-page="5"
            hide-default-footer
            class="history-table"
            :loading="loadingHistory"
          >
            <template v-slot:item.timestamp="{ item }">
              <span class="timestamp-cell">{{ formatTimestamp(item.timestamp) }}</span>
            </template>
            
            <template v-slot:item.device="{ item }">
              <div class="device-info">
                <div class="device-row">
                  <v-icon small :color="getDeviceColor(item.device.type)">
                    {{ getDeviceIcon(item.device.type) }}
                  </v-icon>
                  <span class="device-name">{{ item.device.name }}</span>
                </div>
                <div class="ip-address">{{ item.device.ip }}</div>
                <div class="location-info" v-if="item.device.location">{{ item.device.location }}</div>
              </div>
            </template>
            
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                small
                text-color="white"
              >
                {{ item.status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import { getDocs, query, collection, where, getFirestore, orderBy, limit } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

export default {
  name: "UserProfile",
  data() {
    return {
      loadingHistory: false,
      historyHeaders: [
        { text: 'Timestamp', value: 'timestamp', sortable: false, width: '180px' },
        { text: 'Action', value: 'action', sortable: false, width: '180px' },
        { text: 'Device', value: 'device', sortable: false },
        { text: 'Status', value: 'status', sortable: false, width: '120px' }
      ],
      recentLogins: [],
      userData: {
        fullName: '',
        userID: '',
        role: '',
        email: '',
        department: '',
        phoneNumber: ''
      },
      userDocId: null
    };
  },
  
  async created() {
    await this.checkUser();
  },
  
  methods: {
    getAgentId() {
      return this.userData.role === "Admin" ? "Agent1" : (this.userData.userID || 'NX-2024-001');
    },

    formatTimestamp(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString('en-ZA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    getDeviceIcon(deviceType) {
      const icons = {
        'desktop': 'mdi-desktop-classic',
        'mobile': 'mdi-cellphone',
        'tablet': 'mdi-tablet'
      };
      return icons[deviceType] || 'mdi-help';
    },

    getDeviceColor(deviceType) {
      const colors = {
        'desktop': '#1976d2',
        'mobile': '#388e3c',
        'tablet': '#f57c00'
      };
      return colors[deviceType] || '#757575';
    },

    getStatusColor(status) {
      switch (status) {
        case 'Success': return 'success';
        case 'Blocked': return 'error';
        case 'Warning': return 'warning';
        default: return 'grey';
      }
    },

    async loadUserLoginHistory() {
      try {
        this.loadingHistory = true;
        if (!this.userDocId) return;
        
        const loginQuery = query(
          collection(db, "loginLogs"),
          where("userDocId", "==", this.userDocId),
          orderBy("timestamp", "desc"),
          limit(5)
        );
        
        const loginDocs = await getDocs(loginQuery);
        this.recentLogins = [];
        
        loginDocs.forEach(doc => {
          const data = doc.data();
          const timestamp = data.timestamp?.toDate() || new Date();
          
          this.recentLogins.push({
            id: doc.id,
            timestamp: timestamp.toISOString(),
            action: data.action || 'Login Successful',
            device: {
              type: data.deviceInfo?.deviceType || 'desktop',
              name: `${data.deviceInfo?.os || 'Unknown'} - ${data.deviceInfo?.browser || 'Unknown'}`,
              ip: data.deviceInfo?.ip || '0.0.0.0',
              location: data.deviceInfo?.location || 'Unknown'
            },
            status: data.blocked ? 'Blocked' : 'Success'
          });
        });
        
      } catch (error) {
        console.error('Error loading login history:', error);
      } finally {
        this.loadingHistory = false;
      }
    },

    async checkUser() {
      const user = auth.currentUser;
      if (user) {
        const userQuery = query(collection(db, "users"), where("userID", "==", user.uid));
        const userDocs = await getDocs(userQuery);

        if (!userDocs.empty) {
          const userDoc = userDocs.docs[0];
          this.userData = { ...this.userData, ...userDoc.data() };
          this.userDocId = userDoc.id;
          
          await this.loadUserLoginHistory();
        }
      } else {
        this.$router.push("/");
      }
    }
  }
};
</script>

<style scoped>
.profile-content {
  padding: 20px;
  min-height: 100vh;
  background: #6b7c8d;
}

.main-content {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  background: white;
  padding: 25px 35px;
  border-radius: 12px;
  border-left: 6px solid #d1485c;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
}

.page-title {
  color: #d1485c;
  font-size: 2.2rem;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

.user-info-card,
.history-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.card-header {
  background: #d1485c !important;
  color: white !important;
  padding: 16px 24px !important;
  font-size: 1.2rem;
  font-weight: 600;
}

.info-item {
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 4px;
}

.info-value {
  font-size: 1rem;
  color: #212529;
  font-weight: 600;
}

.history-table {
  border-radius: 0 !important;
}

.timestamp-cell {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  color: #495057;
}

.device-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.device-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.device-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: #495057;
}

.ip-address {
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: #6c757d;
}

.location-info {
  font-size: 0.75rem;
  color: #6c757d;
  font-style: italic;
}

@media (max-width: 768px) {
  .profile-content {
    padding: 10px;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
}
</style>